import { compact, first, map } from 'lodash/fp';
import { RouteName, FeatureFlag } from '@mews-commander/enums';
import {
    mapFormValuesToRoutingRuleAddParameters,
    mapFormValuesToRoutingRuleAssignmentAddParameters,
    mapFormValuesToRoutingRuleUpdateParameters,
    RoutingRuleFormValues,
} from '@mews-commander/routing-rules';
import { AsyncAction, CommanderThunkAction } from '@mews-commander/types';
import { createAction, createAsyncActionTypes, lt, showSuccessAlert } from '@mews/framework';
import { InvalidateQueries } from '@mews-commander/data-access';
import { createBackgroundAction, createFetchAction, getUrlParameter } from '@mews-commander/utils';
import {
    addRoutingRuleAssignments,
    addRoutingRules,
    fetchRoutingRules,
} from '@mews-commander/legacy-entities/src/actions/routingRules';
import {
    fetchAllEnterpriseServices,
    fetchServiceProductCategories,
} from '@mews-commander/legacy-entities/src/actions/services';
import {
    deleteRoutingRuleAssignments,
    getRoutingRuleAssignmentIds,
} from '@mews-commander/legacy-entities/src/ducks/routingRuleAssignments';
import { deleteRoutingRules, updateRoutingRules } from '@mews-commander/legacy-entities/src/ducks/routingRules';
import { getEnterpriseBookableServiceIds } from '@mews-commander/legacy-entities/src/ducks/services';
import { navigateInModal } from '../app/actions/common';
import { prefix } from './module';
import { getRoutingRuleIndexUrl } from './selectors';

export const ROUTING_RULE_ADD = prefix('ROUTING_RULE_ADD');
export const [ROUTING_RULE_ADD_STARTED, ROUTING_RULE_ADD_SUCCEEDED, ROUTING_RULE_ADD_FAILED] =
    createAsyncActionTypes(ROUTING_RULE_ADD);
export const ROUTING_RULE_UPDATE = prefix('ROUTING_RULE_UPDATE');
export const [ROUTING_RULE_UPDATE_STARTED, ROUTING_RULE_UPDATE_SUCCEEDED, ROUTING_RULE_UPDATE_FAILED] =
    createAsyncActionTypes(ROUTING_RULE_UPDATE);
export const ROUTING_RULE_DELETE = prefix('ROUTING_RULE_DELETE');
export const [ROUTING_RULE_DELETE_STARTED, ROUTING_RULE_DELETE_SUCCEEDED, ROUTING_RULE_DELETE_FAILED] =
    createAsyncActionTypes(ROUTING_RULE_DELETE);
export const ROUTING_RULE_FORM_INIT = prefix('ROUTING_RULE_FORM_INIT');
export const [ROUTING_RULE_FORM_INIT_SUCCEEDED, ROUTING_RULE_FORM_INIT_STARTED, ROUTING_RULE_FORM_INIT_FAILED] =
    createAsyncActionTypes(ROUTING_RULE_FORM_INIT);

export const navigateToRoutingRule =
    (routingRuleIds: $TSFixMe, _enterpriseId: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { resolveRoute }: $TSFixMe) => {
        const url = resolveRoute(RouteName.RoutingRuleDetail, {
            routingRuleId: first(routingRuleIds),
        });

        return dispatch(navigateInModal(url));
    };

export const createRoutingRules =
    ({
        enterpriseId,
        values,
    }: {
        enterpriseId: string;
        values: RoutingRuleFormValues;
    }): CommanderThunkAction<AsyncAction> =>
    async (dispatch) => {
        const routingRules = [mapFormValuesToRoutingRuleAddParameters(values)];
        const { selectedServiceId: serviceId } = values;

        const promise = dispatch(addRoutingRules({ routingRules, enterpriseId })).then(
            async ({ value: { routingRuleIds } }) => {
                const routingRuleAssignmentPromises = map(async (routingRuleId) => {
                    const routingRuleAssignments = mapFormValuesToRoutingRuleAssignmentAddParameters(
                        values,
                        // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'string'.
                        routingRuleId,
                        serviceId
                    );
                    return dispatch(addRoutingRuleAssignments({ routingRuleAssignments, enterpriseId }));
                })(routingRuleIds);

                return Promise.all(routingRuleAssignmentPromises).then(() => routingRuleIds);
            }
        );

        return dispatch(createFetchAction(ROUTING_RULE_ADD, promise));
    };

export const editRoutingRule =
    ({ values, id, enterpriseId }: $TSFixMe): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState) => {
        const state = getState();
        const { selectedServiceId: serviceId } = values;
        const routingRuleAssignmentIds = getRoutingRuleAssignmentIds(state, { routingRuleId: id, enterpriseId });

        const routingRuleUpdates = [mapFormValuesToRoutingRuleUpdateParameters(values, id)];

        const promise = dispatch(updateRoutingRules({ routingRuleUpdates, enterpriseId }))
            .then(async () => dispatch(deleteRoutingRuleAssignments({ routingRuleAssignmentIds, enterpriseId })))
            .then(async () => {
                const routingRuleAssignments = mapFormValuesToRoutingRuleAssignmentAddParameters(values, id, serviceId);

                return dispatch(addRoutingRuleAssignments({ routingRuleAssignments, enterpriseId }));
            });

        return dispatch(createBackgroundAction(ROUTING_RULE_UPDATE, promise));
    };

export const fetchFormDetails = async (context: $TSFixMe, { routingRuleId }: $TSFixMe) => {
    const { dispatch, getState } = context;
    const enterpriseId = getUrlParameter('enterpriseId') as string;

    dispatch(createAction(ROUTING_RULE_FORM_INIT_STARTED));

    const servicesPromise = dispatch(fetchAllEnterpriseServices({ enterpriseIds: [enterpriseId] })).then(async () => {
        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
        const serviceIds = getEnterpriseBookableServiceIds(getState(), { enterpriseId });

        const fetchActions = compact([
            dispatch(fetchServiceProductCategories({ serviceIds, enterpriseIds: [enterpriseId] })),
            routingRuleId && dispatch(fetchRoutingRules({ routingRuleIds: [routingRuleId], enterpriseId })),
        ]);

        return Promise.all(fetchActions);
    });

    await servicesPromise
        .then(() => dispatch(createAction(ROUTING_RULE_FORM_INIT_SUCCEEDED)))
        .catch((e: $TSFixMe) => {
            dispatch(createAction(ROUTING_RULE_FORM_INIT_FAILED));
            throw e;
        });
};

interface DeleteRoutingRuleHeadingActionProps {
    routingRuleId: string;
    enterpriseId: string;
    entityTitle: string;
    invalidateQuery: (queries: InvalidateQueries) => Promise<void>[];
}

export const deleteRoutingRuleHeadingAction =
    ({
        routingRuleId,
        enterpriseId,
        entityTitle,
        invalidateQuery,
    }: DeleteRoutingRuleHeadingActionProps): CommanderThunkAction =>
    async (dispatch, getState, { resolveRoute, featureFlagService }) => {
        const state = getState();
        const routingRuleAssignmentIds = getRoutingRuleAssignmentIds(state, { routingRuleId, enterpriseId });
        const isNewRoutingRulesOnAddEnabled = featureFlagService.hasFlag(FeatureFlag.newRoutingRulesOnAddEnabled);
        const deletePromise = dispatch(deleteRoutingRuleAssignments({ routingRuleAssignmentIds, enterpriseId })).then(
            async () => dispatch(deleteRoutingRules({ routingRuleIds: [routingRuleId], enterpriseId }))
        );

        const promise = deletePromise.then(() => {
            showSuccessAlert({
                description: lt('DeleteSuccessMessage', { entity: entityTitle }),
            });
            if (isNewRoutingRulesOnAddEnabled) {
                void invalidateQuery({ routingRules: ['newGetAll'] });
            }

            const url = getRoutingRuleIndexUrl(state, { enterpriseId, resolveRoute });
            dispatch(navigateInModal(url));
        });

        return dispatch(createBackgroundAction(ROUTING_RULE_DELETE, promise));
    };
